import { useMemo } from 'react';
import { createTheme, Theme } from '@mui/material/styles';
import type {} from '@mui/x-date-pickers/themeAugmentation';


const FALLBACK = {
  text: '#111827',
  surface: '#ffffff',
  border: '#c4c4c4',
  radius: '8px',
  font: 'inherit',
  accent: '#0069ff',
  accentText: '#ffffff',
};

const opaqueOr = (value: string | undefined, fallback: string): string => {
  if (!value) return fallback;
  const normalized = value.replace(/\s/g, '');
  if (normalized === 'transparent' || /rgba\(.*,0\)$/.test(normalized)) {
    return fallback;
  }
  return value;
};

export interface PickerPalette {
  text: string;
  surface: string;
  border: string;
  radius: string;
  font: string;
  accent: string;
  accentText: string;
}

export const readPickerPalette = (
  anchor: HTMLElement | null,
): PickerPalette => {
  if (!anchor || typeof window === 'undefined') return { ...FALLBACK };

  const input = window.getComputedStyle(anchor);


  const button = anchor
    .closest('form, .react-form-builder-preview, .form-preview-modal, .form-renderer-container')
    ?.querySelector<HTMLElement>(
      'button[type="submit"], .btn-primary, input[type="submit"]',
    );
  const buttonStyle = button ? window.getComputedStyle(button) : null;

  return {
    text: opaqueOr(input.color, FALLBACK.text),
    surface: opaqueOr(input.backgroundColor, FALLBACK.surface),
    border: opaqueOr(input.borderColor, FALLBACK.border),
    radius: input.borderRadius || FALLBACK.radius,
    font: input.fontFamily || FALLBACK.font,
    accent: opaqueOr(buttonStyle?.backgroundColor, FALLBACK.accent),
    accentText: opaqueOr(buttonStyle?.color, FALLBACK.accentText),
  };
};

export const buildPickerTheme = (palette: PickerPalette): Theme =>
  createTheme({
    typography: { fontFamily: palette.font },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: palette.surface,
            color: palette.text,
            border: `1px solid ${palette.border}`,
            borderRadius: palette.radius,
            backgroundImage: 'none',
          },
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            color: palette.text,
            borderRadius: palette.radius,
            '&:hover': { backgroundColor: `${palette.accent}22` },
            '&.Mui-selected': {
              backgroundColor: palette.accent,
              color: palette.accentText,
              '&:hover': { backgroundColor: palette.accent },
              '&:focus': { backgroundColor: palette.accent },
            },
            '&.Mui-disabled': { color: palette.text, opacity: 0.38 },
          },
          today: { borderColor: palette.accent },
        },
      },
      MuiDayCalendar: {
        styleOverrides: {
          weekDayLabel: { color: palette.text, opacity: 0.7 },
        },
      },
      MuiPickersCalendarHeader: {
        styleOverrides: {
          label: { color: palette.text },
          switchViewIcon: { color: palette.text },
        },
      },
      MuiPickersArrowSwitcher: {
        styleOverrides: {
          button: { color: palette.text },
        },
      },
      MuiYearCalendar: {
        styleOverrides: {
          root: {
            '& .MuiPickersYear-yearButton': {
              color: palette.text,
              borderRadius: palette.radius,
              '&:hover': { backgroundColor: `${palette.accent}22` },
              '&.Mui-selected': {
                backgroundColor: palette.accent,
                color: palette.accentText,
                '&:hover': { backgroundColor: palette.accent },
              },
            },
          },
        },
      },
      MuiMonthCalendar: {
        styleOverrides: {
          root: {
            '& .MuiPickersMonth-monthButton': {
              color: palette.text,
              borderRadius: palette.radius,
              '&:hover': { backgroundColor: `${palette.accent}22` },
              '&.Mui-selected': {
                backgroundColor: palette.accent,
                color: palette.accentText,
                '&:hover': { backgroundColor: palette.accent },
              },
            },
          },
        },
      },
      MuiMultiSectionDigitalClock: {
        styleOverrides: {
          root: { borderColor: palette.border },
        },
      },
      MuiMultiSectionDigitalClockSection: {
        styleOverrides: {
          root: { borderColor: palette.border },
          item: {
            color: palette.text,
            '&:hover': { backgroundColor: `${palette.accent}22` },
            '&.Mui-selected': {
              backgroundColor: palette.accent,
              color: palette.accentText,
              '&:hover': { backgroundColor: palette.accent },
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: palette.text,
            '&:hover': { backgroundColor: `${palette.accent}22` },
            '&.Mui-selected': {
              backgroundColor: palette.accent,
              color: palette.accentText,
              '&:hover': { backgroundColor: palette.accent },
            },
          },
        },
      },
      MuiDivider: {
        styleOverrides: { root: { borderColor: palette.border } },
      },
      MuiButton: {
        styleOverrides: {
          root: { color: palette.accent, textTransform: 'none' },
        },
      },
    },
  });

export const usePickerTheme = (palette: PickerPalette): Theme =>
  useMemo(() => buildPickerTheme(palette), [palette]);
